import cn from 'classnames';
import { formatPriceByStoreLocale } from 'lib/shopify';
import { useUI, useDidMountEffect } from 'hooks';
import announce from 'lib/utils/announce';
import { deliveryCostValue } from 'lib/cart/deliveryCostValue';
import styles from './cartSummary.module.scss';

// #742: the bezorgkosten/totaal block. On desktop it stays pinned to the bottom
// of the scroll area so the price reads as one section with the checkout button
// below it; on mobile it scrolls with the cart so only the checkout button stays
// fixed (JW 2026-06-19).
const CartSummary = ({ data, locale }) => {
  const { isEmpty, subTotalPrice, totalPrice, freeShippingPrice, hasShippingBar, translations } =
    data;
  const { displayCartPopup } = useUI();

  const totalPriceIsValid = totalPrice && totalPrice !== subTotalPrice;

  useDidMountEffect(() => {
    if (!displayCartPopup) return;
    if (isEmpty) {
      announce(translations?.cart?.emptyCart || 'Your cart is empty');
      return;
    }
    const shownPrice = totalPriceIsValid ? totalPrice : subTotalPrice;
    if (!Number(shownPrice)) return;
    announce(
      `${
        translations?.cart?.totalLabel || translations?.cart?.subtotal || 'Total'
      }: ${formatPriceByStoreLocale({ locale, price: Number(shownPrice).toFixed(2) })}`
    );
  }, [isEmpty, subTotalPrice, totalPrice]);

  // "gratis|€6,95" — same achieved|progress format as the progress bar labels;
  // falls back to the old message strings when the Contentful entry is missing
  const { isFree: isFreeShipping, value: shippingValue } = deliveryCostValue({
    amount: subTotalPrice,
    freeShippingPrice,
    hasShippingBar,
    translations,
  });

  return (
    <div className={styles.summary}>
      <div className={styles.costRow}>
        <span className={styles.costLabel}>
          {translations?.cart?.deliveryCostsLabel || ''}
          {!isFreeShipping && translations?.cart?.shippingCostsNote ? (
            <span className={styles.costNote}> {translations.cart.shippingCostsNote}</span>
          ) : null}
        </span>
        <span className={styles.costValueMuted}>{shippingValue}</span>
      </div>
      <div className={styles.costRow}>
        <span className={styles.totalLabel}>
          {translations?.cart?.totalLabel || translations?.cart?.subtotal}
        </span>
        <span className={styles.totalValue}>
          <span className={cn({ [styles.lineThrough]: !isEmpty && totalPriceIsValid })}>
            {formatPriceByStoreLocale({ locale, price: Number(subTotalPrice).toFixed(2) })}
          </span>
          {!isEmpty && totalPriceIsValid && (
            <span className={styles.salesPrice}>
              {formatPriceByStoreLocale({ locale, price: Number(totalPrice).toFixed(2) })}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

export default CartSummary;
